import {
  addElement,
  defaultGenericElement,
  deleteMultipleElements,
  reversionElement,
  type GenericElement,
} from '../../lib/element';
import { type Plugin, type BaseEventPayload } from '../../lib/plugin';
import { distance, pointDistance, type Point } from '../../utils/math';
import {
  handleAdditionOfSinglePoint,
  handleRemovalOfSinglePoint,
  handlePolyPointShortcuts,
  handleDeletePolyPointShortcut,
  restoreLastActiveToolSubMode,
} from '../poly/events';
import { initHandlesFromPoints } from '../poly/utils/bezierHandles';

import { getFreeDrawElementAbsoluteCoords } from './utils/coords';
import { canMergeStrokes, mergeStrokes, simplifyPoints } from './utils/points';
import { getFreedrawShape } from './utils/shape';

import { type GenericFreedrawElement, type FreedrawState } from '.';

const MIN_POINT_DRAW_DISTANCE = 2;

export const createFreedrawEvents = <T extends GenericFreedrawElement>(config: {
  toolName: string;
  elementType: T['type'];
  state: FreedrawState<T>;
  getElementDefaults?: (payload: BaseEventPayload) => Partial<T>;
}): Plugin['events'] => {
  const {
    toolName,
    elementType,
    state,
    getElementDefaults = defaultGenericElement,
  } = config;

  return {
    onPointerDown: (payload) => {
      const { event, helpers, runtimeState, elementState, history } = payload;

      if (
        runtimeState.activeTool !== toolName &&
        runtimeState.activeTool !== 'hand'
      ) {
        state.lastPoint = null;
        state.lastDrawnElement = null;
      }

      const { button, pos } = event;

      if (button === 'left' && runtimeState.activeTool === toolName) {
        if (runtimeState.activeKeyPress?.shift && state.lastPoint) {
          const startPoint = {
            x: state.lastPoint[0],
            y: state.lastPoint[1],
          };
          const endPoint = pos;

          const x = Math.min(startPoint.x, endPoint.x);
          const y = Math.min(startPoint.y, endPoint.y);
          const width = Math.abs(startPoint.x - endPoint.x);
          const height = Math.abs(startPoint.y - endPoint.y);

          const points: Point[] = [
            [startPoint.x - x, startPoint.y - y],
            [endPoint.x - x, endPoint.y - y],
          ];

          let straightLineElement = {
            ...getElementDefaults({
              helpers,
              runtimeState,
              elementState,
              history,
            }),
            originX: x,
            originY: y,
            points,
            x,
            y,
            width,
            height,
            strokeColor: state.newStrokeColor,
            strokeWidth: state.newStrokeWidth,
            strokeCap: state.newStrokeCap,
            type: elementType,
            booleanMode: state.booleanMode,
            shape: { data: '', type: 'line' },
            path: new Path2D(),
          } as T;

          let removedLastDrawnElement = false;

          if (canMergeStrokes(straightLineElement, state.lastDrawnElement)) {
            straightLineElement = mergeStrokes(
              straightLineElement,
              state.lastDrawnElement,
            );

            removedLastDrawnElement = true;
          }

          if (!removedLastDrawnElement) {
            straightLineElement = {
              ...straightLineElement,
              bezierHandles: initHandlesFromPoints(straightLineElement.points),
            };
          }
          straightLineElement.shape = getFreedrawShape(straightLineElement);

          if (removedLastDrawnElement && state.lastDrawnElement) {
            deleteMultipleElements(elementState.elements, [
              state.lastDrawnElement.id,
            ]);
          }

          addElement(elementState.elements, straightLineElement);

          state.lastDrawnElement = straightLineElement;

          state.lastPoint = [endPoint.x, endPoint.y];

          history.push(elementState);
          helpers.renderStaticScene();

          helpers.updateRuntimeStore({ newElement: undefined });
          helpers.hideNewElementCanvas();

          return;
        }

        helpers.updateRuntimeStore({
          newElement: {
            ...getElementDefaults({
              helpers,
              runtimeState,
              elementState,
              history,
            }),
            originX: pos.x,
            originY: pos.y,
            points: [[0, 0]],
            strokeColor: state.newStrokeColor,
            strokeWidth: state.newStrokeWidth,
            strokeCap: state.newStrokeCap,
            type: elementType,
            x: pos.x,
            y: pos.y,
            booleanMode: state.booleanMode,
          } as GenericFreedrawElement,
        });

        helpers.showNewElementCanvas();
        helpers.renderNewElementScene();
      }

      const activeElement = elementState.elements.get(
        elementState.selectedElementIds[0],
      ) as GenericFreedrawElement | undefined;

      if (!activeElement || activeElement.type !== toolName) return;

      handleAdditionOfSinglePoint(payload, activeElement, getFreedrawShape);
      handleRemovalOfSinglePoint(payload, activeElement);
    },
    onPointerMove: ({
      event,
      helpers,
      runtimeState,
      elementState,
      history,
    }) => {
      if (runtimeState.activeTool !== toolName) return;

      const { button, pos } = event;

      if (button === 'left' && runtimeState.newElement?.type === elementType) {
        const element = runtimeState.newElement as GenericFreedrawElement;
        const newPoint: Point = [pos.x - element.x, pos.y - element.y];
        const lastPoint = element.points.at(-1);

        const distanceThreshold =
          MIN_POINT_DRAW_DISTANCE / runtimeState.zoom.value;
        if (
          lastPoint &&
          pointDistance(newPoint, lastPoint) < distanceThreshold
        ) {
          return;
        }

        const newElement = {
          ...element,
          ...reversionElement(element, ['bounds', 'canvas']),
          points: [...element.points, newPoint],
        } as GenericElement;

        const [x1, y1, x2, y2] = getFreeDrawElementAbsoluteCoords(
          newElement as T,
        );
        const width = distance(x1, x2);
        const height = distance(y1, y2);

        helpers.updateRuntimeStore({
          newElement: { ...newElement, height, width },
        });
        helpers.renderNewElementScene();
        return;
      }

      if (runtimeState.activeKeyPress?.shift && state.lastPoint) {
        const startPoint = {
          x: state.lastPoint[0],
          y: state.lastPoint[1],
        };
        const endPoint = pos;
        const x = Math.min(startPoint.x, endPoint.x);
        const y = Math.min(startPoint.y, endPoint.y);
        const width = Math.abs(startPoint.x - endPoint.x);
        const height = Math.abs(startPoint.y - endPoint.y);
        const points: Point[] = [
          [startPoint.x - x, startPoint.y - y],
          [endPoint.x - x, endPoint.y - y],
        ];

        const previewElement = {
          ...getElementDefaults({
            helpers,
            runtimeState,
            elementState,
            history,
          }),
          originX: x,
          originY: y,
          points,
          x,
          y,
          width,
          height,
          strokeColor: state.newStrokeColor,
          strokeWidth: state.newStrokeWidth,
          strokeCap: state.newStrokeCap,
          type: elementType,
          booleanMode: state.booleanMode,
          shape: { data: '', type: 'line' },
          path: new Path2D(),
        } as T;

        helpers.updateRuntimeStore({ newElement: previewElement });
        helpers.showNewElementCanvas();
        helpers.renderNewElementScene();
      } else if (runtimeState.newElement) {
        helpers.updateRuntimeStore({ newElement: undefined });
        helpers.hideNewElementCanvas();
      }
    },
    onPointerUp: ({ elementState, helpers, history, runtimeState }) => {
      if (!runtimeState.newElement || runtimeState.activeTool !== toolName)
        return;

      const newElement = runtimeState.newElement as T;

      const simplified = simplifyPoints(newElement.points);

      const freedrawElement: T = {
        ...newElement,
        points: simplified.points,
        bezierHandles: simplified.bezierHandles,
        shape: getFreedrawShape({ ...newElement, ...simplified }),
      };

      addElement(elementState.elements, freedrawElement);
      state.lastDrawnElement = freedrawElement;

      const lastRelativePoint = freedrawElement.points.at(-1)!;
      state.lastPoint = [
        lastRelativePoint[0] + freedrawElement.x,
        lastRelativePoint[1] + freedrawElement.y,
      ];

      history.push(elementState);
      helpers.updateRuntimeStore({ newElement: undefined });

      helpers.renderStaticScene();
      helpers.hideNewElementCanvas();
    },
    onKeyDown: (payload) => {
      handlePolyPointShortcuts<T>(payload, toolName);
      handleDeletePolyPointShortcut<T>(payload, toolName);
    },
    onKeyUp: (payload) => {
      restoreLastActiveToolSubMode(payload, toolName);
    },
  };
};
